import { ReactElement, useMemo, useState } from "react";
import { SearchInput, SearchPlaceholder } from "client/Components";
import { ActiveTab, UserAndUserGroupTabSelector } from "../UserAndUserGroupTabSelector/UserAndUserGroupTabSelector";
import {
	UserColumnToShow,
	UserGroupOverview,
	userGroupOverviewSearchPropertyKeys,
	UserGroupsOverviewSelectionProps
} from "../UserGroupOverview";
import {
	UserOverview,
	UserOverviewSelectionProps,
	userOverviewSearchPropertyKeys
} from "../UserOverview";
import styles from "./UserAndUserGroupOverviews.module.less";
import {
	UsersAndUserGroupsDataModel,
	UserGroupOverviewModel
} from "client/Api/UserGroupsManagement/userGroupsManagement";
import { isNil, setTrackingId } from "client/Misc/utils";
import { UserGridItem } from "client/Api/UserManagement/userManagement";
import { UserLicense } from "client/Api/FeaturePackages/featurePackageManagement";
import { localize } from "client_legacy/ndaw/culture";

export type UserAndUserGroupSelection = {
	users: readonly UserGridItem[];
	userGroups: readonly UserGroupOverviewModel[];
};

export type UserAndUserGroupOverviewsSelectionProps = {
	selection: UserAndUserGroupSelection;
	onSelectionChange(selection: UserAndUserGroupSelection): void;
} | {
	selection?: undefined;
	onSelectionChange?: undefined;
};

export type UserAndUserGroupOverviewsProps = {
	activeTab: ActiveTab;
	onActiveTabChanged(tab: ActiveTab): void;
	data: UsersAndUserGroupsDataModel;
	userGroupUserColumnToShow?: UserColumnToShow;
	requiredUserLicense?: UserLicense | null;
} & UserAndUserGroupOverviewsSelectionProps;

export const UserAndUserGroupOverviews = (props: UserAndUserGroupOverviewsProps): ReactElement => {
	const {
		activeTab,
		onActiveTabChanged,
		data,
		selection,
		userGroupUserColumnToShow,
		requiredUserLicense,
		onSelectionChange
	} = props;

	const [searchTerm, setSearchTerm] = useState<string>("");

	const userSelectionProps = useMemo(
		(): UserOverviewSelectionProps => {
			if (isNil(selection) || isNil(onSelectionChange)) {
				return {
					selection: undefined,
					onSelectionChange: undefined
				};
			}

			return {
				selection: selection.users,
				onSelectionChange: (users: readonly UserGridItem[]): void => {
					if (selection.users !== users) {
						onSelectionChange({
							...selection,
							users
						});
					}
				}
			};
		},
		[selection, onSelectionChange]
	);

	const userGroupSelectionProps = useMemo(
		(): UserGroupsOverviewSelectionProps => {
			if (isNil(selection) || isNil(onSelectionChange)) {
				return {
					selection: undefined,
					onSelectionChange: undefined
				};
			}

			return {
				selection: selection.userGroups,
				onSelectionChange: (userGroups: readonly UserGroupOverviewModel[]): void => {
					if (selection.userGroups !== userGroups) {
						onSelectionChange({
							...selection,
							userGroups
						});
					}
				}
			};
		},
		[selection, onSelectionChange]
	);

	return (
		<div {...setTrackingId("user-and-user-group-overviews")} className={styles.root} >
			<div className={styles.searchAndTabSelection}>
				<div className={styles.search}>
					<SearchInput
						trackingId="search"
						onChange={setSearchTerm}
						icon="filter"
						height="large"
						placeholder={(
							<SearchPlaceholder
								kind="filter"
								properties={
									activeTabToPropertiesMap[activeTab]
										.map(localize)
								}
							/>
						)}
					/>
				</div>
				<div {...setTrackingId("tab-selector")} className={styles.tabSelector}>
					<UserAndUserGroupTabSelector
						value={activeTab}
						onChange={onActiveTabChanged}
					/>
				</div>
			</div>

			<div className={styles.tableContainer}>
				{activeTab === ActiveTab.UserGroup && (
					<UserGroupOverview
						{...userGroupSelectionProps}
						data={data.userGroups}
						searchTerm={searchTerm}
						userColumnToShow={userGroupUserColumnToShow}
						height="stretch"
					/>
				)}

				{activeTab === ActiveTab.User && (
					<UserOverview
						{...userSelectionProps}
						data={data.users}
						searchTerm={searchTerm}
						height="stretch"
						requiredUserLicense={requiredUserLicense}
					/>
				)}
			</div>
		</div>
	);
};
UserAndUserGroupOverviews.displayName = "UserAndUserGroupOverviewGrids";

const activeTabToPropertiesMap: Record<ActiveTab, readonly [LocalizationKey, ...LocalizationKey[]]> = {
	[ActiveTab.User]: userOverviewSearchPropertyKeys,
	[ActiveTab.UserGroup]: userGroupOverviewSearchPropertyKeys
};
